import { useEffect } from "react";

/**
 * useSeo — lightweight SEO tag manager. Sets document.title and updates
 * (or creates) the meta description, og:title, og:description, canonical
 * and twitter card tags for the current route. Restores previous values
 * on unmount so navigation between pages leaves no bleed.
 */
export function useSeo({ title, description, canonical, image, type = "website" } = {}) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta("name", "description"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogType: getMeta("property", "og:type"),
      ogImage: getMeta("property", "og:image"),
      twitter: getMeta("name", "twitter:card"),
      canonical: getLinkHref("canonical"),
    };

    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (title) setMeta("property", "og:title", title);
    if (description) setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    if (image) setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    if (canonical) setLink("canonical", canonical);

    return () => {
      document.title = prev.title;
      if (prev.description !== null) setMeta("name", "description", prev.description);
      if (prev.ogTitle !== null) setMeta("property", "og:title", prev.ogTitle);
      if (prev.ogDescription !== null) setMeta("property", "og:description", prev.ogDescription);
      if (prev.ogType !== null) setMeta("property", "og:type", prev.ogType);
      if (prev.ogImage !== null) setMeta("property", "og:image", prev.ogImage);
      if (prev.twitter !== null) setMeta("name", "twitter:card", prev.twitter);
      if (prev.canonical !== null) setLink("canonical", prev.canonical);
    };
  }, [title, description, canonical, image, type]);
}

function getMeta(attr, value) {
  const el = document.querySelector(`meta[${attr}="${value}"]`);
  return el ? el.getAttribute("content") : null;
}
function setMeta(attr, value, content) {
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function getLinkHref(rel) {
  const el = document.querySelector(`link[rel="${rel}"]`);
  return el ? el.getAttribute("href") : null;
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
